import React, { useState } from 'react';
import { Grid, TextField, Checkbox, FormControlLabel, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { format, isAfter,startOfDay, isValid } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

dayjs.extend(utc);
dayjs.extend(timezone);

const DateRangeView = ({
    isDisabled,
    start,
    setStart,
    end,
    setEnd,
    userTimeZone // Ensure you pass the user's timezone as a prop
}) => {
    const [isIndefinite, setIsIndefinite] = useState(!end);
    const [dateError, setDateError] = useState('');

    const handleIndefiniteToggle = () => {
        setIsIndefinite((prev) => {
            const newIndefinite = !prev;
            if (newIndefinite) {
                setEnd(null); // Clear the end date if indefinite is checked
            }
            return newIndefinite;
        });
    };

    // Format date to display in local timezone
    const formatDateToLocal = (date) => {
        return date ? format(utcToZonedTime(date, userTimeZone), 'yyyy-MM-dd') : '';
    };

    const handleStartDateChange = (e) => {
        const userInputDate = e.target.value;
        const [year, month, day] = userInputDate.split('-');
        const localStartDate = startOfDay(new Date(year, month - 1, day));

        // Validate user input date
        if (!isValid(localStartDate)) {
            setDateError('Invalid start date.');
            return;
        }

        // Convert local time to UTC for saving
        const utcStartDate = zonedTimeToUtc(localStartDate, userTimeZone).toISOString();
        setStart(utcStartDate);

        // Compare only the date portion
        if (end) {

            const localEndDate = utcToZonedTime(new Date(end), userTimeZone);
            if (isAfter(startOfDay(localStartDate), startOfDay(localEndDate))) {
                setDateError('End date cannot be before the start date.');
            } else {
                setDateError('');
            }
        } else {
            setDateError('');
        }
    };

    const handleEndDateChange = (e) => {
        const userInputDate = e.target.value;
        const [year, month, day] = userInputDate.split('-');
        const localEndDate = startOfDay(new Date(year, month - 1, day));

        // Validate user input date
        if (!isValid(localEndDate)) {
            setDateError('Invalid end date.');
            return;
        }

        // Convert local time to UTC for saving
        const utcEndDate = zonedTimeToUtc(localEndDate, userTimeZone).toISOString();

        // Compare only the date portion
        if (start) {
            const localStartDate = utcToZonedTime(new Date(start), userTimeZone);
            if (isAfter(localStartDate, localEndDate)) {
                setDateError('End date cannot be before the start date.');
            } else {
                setEnd(utcEndDate);
                setDateError('');
            }
        } else {
            setEnd(utcEndDate);
            setDateError('');
        }
    };

    return (
        <Grid container alignItems="center" spacing={2}>
            {/* Start Date Input */}
            <Grid item xs={12}>
                <TextField
                    label="Start Date"
                    type="date"
                    value={formatDateToLocal(start)} // Display in local timezone
                    onChange={handleStartDateChange}
                    variant="outlined"
                    disabled={isDisabled}
                    fullWidth
                    InputProps={{
                        sx: {
                            maxWidth: '300px',
                        },
                    }}
                    sx={{
                        borderRadius: '8px',
                        '& .MuiInputBase-root': { color: '#333' },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                            '&:hover fieldset': { borderColor: '#888' },
                            '&.Mui-focused fieldset': { borderColor: '#4caf50' },
                        },
                    }}
                    InputLabelProps={{
                        sx: { color: '#333' },
                    }}
                />
            </Grid>

            {/* End Date Input + No End Date Checkbox */}
            <Grid item xs={12}>
                <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={7}>
                        <TextField
                            type={isIndefinite ? '' : 'date'}
                            label={isIndefinite ? '' : 'End Date'}
                            value={isIndefinite ? 'Never End' : formatDateToLocal(end)} // Display in local timezone
                            onChange={handleEndDateChange}
                            disabled={isIndefinite || isDisabled}
                            fullWidth
                            InputProps={{
                                sx: {
                                    maxWidth: '300px',
                                },
                            }}
                            sx={{
                                borderRadius: '8px',
                                '& .MuiInputBase-root': { color: '#333' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
                                    '&:hover fieldset': { borderColor: '#888' },
                                    '&.Mui-focused fieldset': { borderColor: '#4caf50' },
                                },
                            }}
                            InputLabelProps={{
                                sx: { color: '#333' },
                            }}
                        />
                        {dateError && (
                            <Typography variant="body2" color="error">
                                {dateError}
                            </Typography>
                        )}
                    </Grid>

                    {/* No End Date Checkbox */}
                    <Grid
                        item
                        xs={5}
                        sx={{ display: 'flex', alignItems: 'center', marginLeft: '-15px' }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isIndefinite}
                                    onChange={handleIndefiniteToggle}
                                />
                            }
                            label="Never End"
                            sx={{ marginLeft: '2px', whiteSpace: 'nowrap' }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default DateRangeView;
